import { useState } from 'react';
import { useSiteData } from '@/contexts/SiteDataContext';
import type { GalleryImage } from '@/mocks/galleryData';

const CATEGORIES = ['Community Events', 'Education', 'Healthcare', 'Food Relief', 'Women Empowerment', 'Environment', 'Disaster Relief', 'Animal Welfare'];

const emptyImage: GalleryImage = {
  id: Date.now(),
  src: '',
  alt: '',
  category: 'Community Events',
  location: 'Hyderabad, Telangana',
  date: new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
};

export default function GalleryManageSection() {
  const { data, updateGalleryImages } = useSiteData();
  const [images, setImages] = useState<GalleryImage[]>([...data.galleryImages]);
  const [saved, setSaved] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newImg, setNewImg] = useState<GalleryImage>({ ...emptyImage });
  const [filterCat, setFilterCat] = useState('All');
  const [editId, setEditId] = useState<number | null>(null);

  const handleSave = () => {
    updateGalleryImages(images);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const addImage = () => {
    if (!newImg.src || !newImg.alt) return;
    setImages([{ ...newImg, id: Date.now() }, ...images]);
    setNewImg({ ...emptyImage, id: Date.now() });
    setAdding(false);
  };

  const removeImage = (id: number) => setImages(images.filter((img) => img.id !== id));

  const updateImage = (id: number, field: keyof GalleryImage, value: string) => {
    setImages(images.map((img) => img.id === id ? { ...img, [field]: value } : img));
  };

  const filtered = filterCat === 'All' ? images : images.filter((i) => i.category === filterCat);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-foreground-900 mb-1">Gallery Images</h2>
          <p className="text-foreground-500 text-sm">Add, remove, or edit images shown in the Gallery page. ({images.length} total)</p>
        </div>
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary-500 text-white rounded-xl text-sm font-semibold hover:bg-secondary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-image-add-line"></i> Add Image
        </button>
      </div>

      {adding && (
        <div className="border-2 border-dashed border-primary-300 rounded-2xl p-5 bg-primary-50 space-y-4">
          <p className="text-sm font-bold text-primary-700">New Image</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-foreground-600 mb-1">Image URL *</label>
              <input type="text" value={newImg.src} onChange={(e) => setNewImg({ ...newImg, src: e.target.value })} placeholder="https://..." className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1">Alt Text / Caption *</label>
              <input type="text" value={newImg.alt} onChange={(e) => setNewImg({ ...newImg, alt: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1">Category</label>
              <select value={newImg.category} onChange={(e) => setNewImg({ ...newImg, category: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1">Location</label>
              <input type="text" value={newImg.location} onChange={(e) => setNewImg({ ...newImg, location: e.target.value })} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1">Date</label>
              <input type="text" value={newImg.date} onChange={(e) => setNewImg({ ...newImg, date: e.target.value })} placeholder="July 2026" className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300" />
            </div>
          </div>
          {newImg.src && <img src={newImg.src} alt="" className="h-28 rounded-xl object-cover" />}
          <div className="flex gap-3">
            <button onClick={addImage} className="px-5 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">Add to Gallery</button>
            <button onClick={() => setAdding(false)} className="px-5 py-2.5 bg-background-200 text-foreground-700 rounded-xl text-sm font-semibold hover:bg-background-300 transition-colors whitespace-nowrap">Cancel</button>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['All', ...CATEGORIES].map((cat) => (
          <button key={cat} onClick={() => setFilterCat(cat)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${filterCat === cat ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-primary-50'}`}>{cat}</button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {filtered.map((img) => (
          <div key={img.id} className="group relative rounded-xl overflow-hidden border border-background-200 bg-background-50">
            <div className="aspect-square relative">
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button
                  onClick={() => setEditId(editId === img.id ? null : img.id)}
                  className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-edit-line text-sm"></i>
                </button>
                <button
                  onClick={() => removeImage(img.id)}
                  className="w-8 h-8 rounded-full bg-rose-500/80 hover:bg-rose-600 text-white flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            </div>
            <div className="p-2">
              <p className="text-[10px] text-accent-600 font-semibold">{img.category}</p>
              <p className="text-xs text-foreground-700 font-medium leading-snug truncate">{img.alt}</p>
              <p className="text-[10px] text-foreground-400">{img.location} · {img.date}</p>
            </div>
            {editId === img.id && (
              <div className="p-3 border-t border-background-200 bg-white space-y-2">
                <input type="text" value={img.alt} onChange={(e) => updateImage(img.id, 'alt', e.target.value)} placeholder="Alt text" className="w-full px-2 py-1.5 text-xs rounded-lg border border-background-200 focus:outline-none focus:ring-1 focus:ring-primary-300" />
                <select value={img.category} onChange={(e) => updateImage(img.id, 'category', e.target.value)} className="w-full px-2 py-1.5 text-xs rounded-lg border border-background-200 focus:outline-none cursor-pointer">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
                <input type="text" value={img.location} onChange={(e) => updateImage(img.id, 'location', e.target.value)} placeholder="Location" className="w-full px-2 py-1.5 text-xs rounded-lg border border-background-200 focus:outline-none focus:ring-1 focus:ring-primary-300" />
              </div>
            )}
          </div>
        ))}
      </div>

      <button onClick={handleSave} className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap ${saved ? 'bg-emerald-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'}`}>
        <i className={saved ? 'ri-check-line' : 'ri-save-line'}></i>
        {saved ? 'Saved!' : `Save Gallery (${images.length} images)`}
      </button>
    </div>
  );
}